import { useState } from "react";
import { ArrowDown, ArrowUp, CheckCircle2, ChevronDown, HelpCircle, Link2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const input = "min-h-[44px] w-full min-w-0 rounded-lg border border-neutral-700 bg-black/50 px-3 py-2 text-sm text-white";

// 3-line explainer + « ? » opening the slightly longer how-to.
export const CartIntro = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-w-0 rounded-xl border border-[rgba(245,197,66,0.35)] bg-black/30 p-3" data-testid="cart-intro">
      <div className="flex min-w-0 items-start gap-2">
        <p className="min-w-0 flex-1 break-words text-sm text-neutral-200" data-testid="cart-intro-text">{t("cart.intro")}</p>
        <button type="button" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--gold)] text-[var(--gold)]"
          onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={t("cart.helpBtn")} data-testid="cart-help-toggle"><HelpCircle className="h-4 w-4" /></button>
      </div>
      {open && <p className="mt-2 whitespace-pre-line break-words text-xs text-neutral-300" data-testid="cart-help">{t("cart.help")}</p>}
    </div>
  );
};

const move = (list, i, d) => {
  const j = i + d;
  if (j < 0 || j >= list.length) return list;
  const out = [...list];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
};

// Auto-detected businesses (registered by the mini-site or linked): one tap to confirm the list and its order.
export const ConfirmList = ({ rows, onDone }) => {
  const { t } = useI18n();
  const [order, setOrder] = useState(rows);
  const [busy, setBusy] = useState(false);
  const ok = async () => {
    setBusy(true);
    try {
      await api.put("/dev/billing/cart/confirm", { items: order.map((b, i) => ({ key_id: b.key_id, ref: b.ref, position: i + 1 })) });
      onDone();
    } catch (err) { toast.error(translateError(err, t)); }
    setBusy(false);
  };
  return (
    <div className="min-w-0 space-y-3 rounded-xl border border-emerald-500/50 bg-emerald-950/30 p-3" data-testid="setup-confirm">
      <p className="break-words text-sm font-semibold text-white">{t("setup.found", { n: order.length })}</p>
      <ol className="min-w-0 space-y-1">
        {order.map((b, i) => (
          <li key={`${b.key_id}:${b.ref}`} className="flex min-w-0 items-center gap-2 text-sm text-neutral-200" data-testid={`setup-confirm-row-${b.ref}`}>
            <span className="shrink-0 font-bold text-[var(--gold)]">{t("setup.pos", { n: i + 1 })}</span>
            <span className="min-w-0 flex-1 truncate">{b.business_name || b.ref}</span>
            <button type="button" className="p-2 text-neutral-400" onClick={() => setOrder(move(order, i, -1))} aria-label={t("setup.up")} data-testid={`setup-up-${b.ref}`}><ArrowUp className="h-4 w-4" /></button>
            <button type="button" className="p-2 text-neutral-400" onClick={() => setOrder(move(order, i, 1))} aria-label={t("setup.down")} data-testid={`setup-down-${b.ref}`}><ArrowDown className="h-4 w-4" /></button>
          </li>
        ))}
      </ol>
      <button type="button" className="btn-gold btn-sm min-h-[44px] w-full justify-center sm:w-auto" onClick={ok} disabled={busy} data-testid="setup-confirm-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t("setup.confirm")}
      </button>
    </div>
  );
};

// Add a business with a plain form: the REF is generated by the store, never typed.
export const AddBusiness = ({ app, next, onDone }) => {
  const { t } = useI18n();
  const [f, setF] = useState({ business_name: "", email: "", phone: "", position: next });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/dev/billing/cart/setup", { app, items: [{ ...f, email: f.email || null, phone: f.phone || null, position: Number(f.position) || next }] });
      toast.success(t("setup.added"));
      setF({ business_name: "", email: "", phone: "", position: next + 1 });
      onDone();
    } catch (err) { toast.error(translateError(err, t)); }
    setBusy(false);
  };
  return (
    <form onSubmit={add} className="grid min-w-0 gap-2 sm:grid-cols-2" data-testid="setup-add-form">
      <input className={input} required maxLength={120} placeholder={t("setup.name")} value={f.business_name} onChange={set("business_name")} data-testid="setup-add-name" />
      <input className={input} type="email" maxLength={200} placeholder={t("setup.email")} value={f.email} onChange={set("email")} data-testid="setup-add-email" />
      <input className={input} type="tel" maxLength={40} placeholder={t("setup.phone")} value={f.phone} onChange={set("phone")} data-testid="setup-add-phone" />
      <label className="flex min-w-0 items-center gap-2 text-sm text-neutral-300">{t("setup.position")}
        <input className={`${input} w-24`} type="number" min={1} max={999} value={f.position} onChange={set("position")} data-testid="setup-add-position" /></label>
      <button type="submit" className="btn-red btn-sm min-h-[44px] justify-center sm:col-span-2 sm:w-auto" disabled={busy || !f.business_name.trim()} data-testid="setup-add-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("setup.save")}
      </button>
    </form>
  );
};

// Power users only (hidden by default): a REF + the link signature given by the mini-site.
export const AdvancedLink = ({ app, onDone }) => {
  const { t } = useI18n();
  const [f, setF] = useState({ ref: "", email: "", sig: "" });
  const link = async (e) => {
    e.preventDefault();
    try {
      await api.post("/dev/billing/cart/link", { app, refs: [f.ref.trim().toUpperCase()], email: f.email.trim(), sig: f.sig.trim() });
      toast.success(t("setup.linked"));
      onDone();
    } catch (err) { toast.error(translateError(err, t)); }
  };
  return (
    <details className="min-w-0 text-sm text-neutral-300" data-testid="setup-advanced">
      <summary className="flex min-h-[44px] cursor-pointer list-none items-center gap-2 text-xs text-neutral-400" data-testid="setup-advanced-toggle">
        <ChevronDown className="h-4 w-4 shrink-0" /> {t("setup.advanced")}</summary>
      <form onSubmit={link} className="mt-2 grid min-w-0 gap-2 sm:grid-cols-3">
        <input className={input} required maxLength={40} placeholder="REF" value={f.ref} onChange={(e) => setF({ ...f, ref: e.target.value })} data-testid="setup-advanced-ref" />
        <input className={input} type="email" required maxLength={200} placeholder={t("setup.email")} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="setup-advanced-email" />
        <input className={input} required maxLength={128} placeholder={t("setup.sig")} value={f.sig} onChange={(e) => setF({ ...f, sig: e.target.value })} data-testid="setup-advanced-sig" />
        <button type="submit" className="btn-red btn-sm min-h-[44px] justify-center sm:col-span-3 sm:w-auto" data-testid="setup-advanced-submit"><Link2 className="h-4 w-4" /> {t("setup.link")}</button>
      </form>
    </details>
  );
};
